import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { Building2, ChevronDown, Check, Plus, X, Lock } from 'lucide-react';
import { selectOwnedCompanies, selectActiveCompanyId, setOwnedCompanies, setActiveCompanyId } from '../../store/slices/workspaceSlice';
import { setAuth } from '../../store/slices/authSlice';
import { setCompany } from '../../store/slices/companySlice';
import { useSwitchCompany, useAddCompany } from '../../api/queries/workspace.queries';
import { INDIAN_STATES } from '../../lib/constants';
import { selectCompanyPlan, selectCompanyName } from '../../store/slices/companySlice';

function AddCompanyModal({ onClose, onAdded }) {
  const { mutate: addCompany, isPending } = useAddCompany();
  const [name, setName] = useState('');
  const [state, setState] = useState('MH');
  const inputCls = 'w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';

  const handleSubmit = () => {
    if (!name.trim()) { toast.error('Enter a company name'); return; }
    addCompany({ company_name: name.trim(), state }, {
      onSuccess: (res) => { toast.success(`${name.trim()} added`); onAdded(res.data); },
      onError: () => toast.error('Failed to add company'),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <h2 className="text-[16px] font-bold text-[var(--text-primary)]">Add Company</h2>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-[12px] text-[var(--text-secondary)]">Adds another fully-isolated company under your Custom account — no new phone/OTP needed.</p>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Company Name</label>
            <input className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Greenfield Exports" autoFocus />
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">State</label>
            <select className={inputCls} value={state} onChange={e => setState(e.target.value)}>
              {INDIAN_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </div>
          <button onClick={handleSubmit} disabled={isPending}
            className="w-full h-10 rounded-[10px] bg-[var(--accent)] text-white text-[14px] font-semibold disabled:opacity-60">
            {isPending ? 'Adding…' : 'Add Company'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Shown for Basic/Pro plans (no multi-company access yet) — visible so the
// feature is discoverable, but blurred/disabled with an upsell nudge
// instead of being hidden entirely.
function LockedAddCompany() {
  const navigate = useNavigate();
  return (
    <button onClick={() => { toast.info('Multi-company support is a Custom plan feature'); navigate('/settings/plan'); }}
      className="group relative flex items-center gap-2 h-9 px-3 rounded-[10px] border border-[var(--border)] text-[var(--text-tertiary)] cursor-pointer transition-colors hover:border-[var(--accent)]/40">
      <span className="flex items-center gap-2 blur-[2.5px] opacity-60 group-hover:opacity-40 transition-opacity select-none pointer-events-none">
        <Building2 size={15} />
        <span className="text-[13px] font-semibold">Add Company</span>
      </span>
      <Lock size={12} className="absolute right-2.5 text-[var(--text-tertiary)]" />
    </button>
  );
}

export function WorkspaceSwitcher() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ownedCompanies = useSelector(selectOwnedCompanies);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const plan = useSelector(selectCompanyPlan);
  const companyName = useSelector(selectCompanyName);
  const { mutate: switchCompany, isPending: switching } = useSwitchCompany();
  const [open, setOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  // Only CUSTOM plan accounts can have multiple companies
  if (plan !== 'CUSTOM') return <LockedAddCompany />;

  // Fallback to Redux company name if ownedCompanies isn't loaded yet
  const active = ownedCompanies.find(c => c.id === activeCompanyId) || ownedCompanies[0] || { name: companyName, id: activeCompanyId };

  const handleSwitch = (companyId) => {
    if (companyId === activeCompanyId) { setOpen(false); return; }
    switchCompany(companyId, {
      onSuccess: (res) => {
        const { access_token, user, company, permissions, owned_companies } = res.data;
        dispatch(setAuth({ user, companyId: company.id, accessToken: access_token, permissions }));
        dispatch(setCompany(company));
        dispatch(setOwnedCompanies(owned_companies || ownedCompanies));
        dispatch(setActiveCompanyId(company.id));
        setOpen(false);
        toast.success(`Switched to ${company.name}`);
        queryClient.clear();
        navigate('/dashboard');
      },
      onError: () => toast.error('Failed to switch company'),
    });
  };

  const handleAdded = (data) => {
    dispatch(setOwnedCompanies(data.owned_companies));
    setShowAdd(false);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} disabled={switching}
        className="flex items-center gap-2 h-9 px-3 rounded-[10px] border border-[var(--border)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] transition-colors max-w-[200px]">
        <Building2 size={15} className="text-[var(--text-secondary)] shrink-0" />
        <span className="text-[13px] font-semibold truncate">{active?.name}</span>
        <ChevronDown size={14} className="text-[var(--text-tertiary)] shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1.5 left-0 w-64 bg-[var(--bg-card)] border border-[var(--border)] rounded-[14px] shadow-[var(--shadow-lg)] z-50 overflow-hidden fade-in">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-tertiary)] px-3 pt-3 pb-1.5">Your companies</p>
            <div className="max-h-60 overflow-y-auto px-1.5 pb-1.5">
              {ownedCompanies.map(c => (
                <button key={c.id} onClick={() => handleSwitch(c.id)}
                  className="w-full flex items-center gap-2.5 px-2.5 h-10 rounded-[10px] hover:bg-[var(--bg-hover)] text-left transition-colors">
                  <div className="w-7 h-7 rounded-[8px] bg-[var(--accent-muted)] flex items-center justify-center text-[var(--accent)] text-[12px] font-bold shrink-0">
                    {c.name?.charAt(0)}
                  </div>
                  <span className="flex-1 text-[13px] font-medium text-[var(--text-primary)] truncate">{c.name}</span>
                  {c.id === activeCompanyId && <Check size={14} className="text-[var(--accent)] shrink-0" />}
                </button>
              ))}
            </div>
            <div className="border-t border-[var(--border)] p-1.5">
              <button onClick={() => { setShowAdd(true); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-2.5 h-9 rounded-[10px] hover:bg-[var(--bg-hover)] text-[13px] font-medium text-[var(--accent)] transition-colors">
                <Plus size={15} /> Add Company
              </button>
            </div>
          </div>
        </>
      )}

      {showAdd && <AddCompanyModal onClose={() => setShowAdd(false)} onAdded={handleAdded} />}
    </div>
  );
}

export default WorkspaceSwitcher;
